import type {
  Absence,
  Appointment,
  ScheduleEntry,
  ScheduleEntryType,
  TaskListItem,
} from '@myapp/contract';
import { getAbsenceCategoryLabel } from '../../../entities/absence';
import { buildReschedulingLabel } from '../../../entities/schedule';
import { formatTimeHHmm, resolveBlockTimes, workWindow } from '../../../shared/lib/schedule';
import { showsReschedulingState } from './reschedulingDisplay';

/**
 * The hover text shared by both views. Lives here rather than in `useDispositionBoard` because the
 * week grid needs the same lines: its chips carry two lines of text at most, so the tooltip is the
 * only place the rest of an entry is readable.
 *
 * The range is the **work** window. The occupancy block — Anfahrt to Abfahrt — is what the day
 * board draws, not what it says: every clock time on this board that a human reads is a work time.
 * The travel minutes are still visible on the day board's own Anfahrt strip.
 */

const KIND_FALLBACK_LABEL: Record<ScheduleEntryType, string> = {
  APPOINTMENT: 'Termin',
  TASK: 'Aufgabe',
  ABSENCE: 'Abwesenheit',
  BREAK: 'Pause',
};

/**
 * `Braucht Umdisponierung: Krankheit`. Thin wrapper over the shared helper, kept because the
 * board's callers pass a whole entry rather than the two fields.
 *
 * The suffix comes from `reschedulingCause` first — `reschedulingReason` only ever answered
 * "which absence displaced it", which says nothing about a block force-planned onto a Saturday.
 */
export function buildReschedulingLine(
  entry: Pick<ScheduleEntry, 'reschedulingCause' | 'reschedulingReason'>,
): string {
  return buildReschedulingLabel(entry.reschedulingCause, entry.reschedulingReason);
}

/** Everything a block cannot fit, until the hover popover is rebuilt. */
export function buildTooltip(
  entry: ScheduleEntry,
  appointment: Appointment | null,
  task: TaskListItem | null,
  absence: Absence | null,
): string {
  const work = workWindow(entry);
  const range = `${formatTimeHHmm(work.startsAt)}–${formatTimeHHmm(work.endsAt)}`;
  // On the time line, not a line of its own: `ScheduleBlock`'s aria-label reads the first two
  // lines and the rescheduling line must stay third, so the phase rides along with the range.
  const { phaseLabel } = resolveBlockTimes(entry);
  const time = phaseLabel ? `${range} · ${phaseLabel}` : range;
  const external = appointment?.workOrder?.external;

  const lines = [
    time,
    entry.label ?? KIND_FALLBACK_LABEL[entry.type],
    // Third, not last: the reason (`Krankmeldung`, `Urlaub` — the category of the absence that
    // displaced this block) is the first thing a dispatcher needs before deciding where it goes.
    showsReschedulingState(entry) ? buildReschedulingLine(entry) : null,
    external?.hauseingang,
    external?.veAdresse,
    external?.gebaeude,
    external?.gewerkeText,
    external?.leistungText,
    external?.mieter.name,
    external?.mieter.telefon,
    task?.title,
    absence ? getAbsenceCategoryLabel(absence.category) : null,
  ];

  return lines.filter(Boolean).join('\n');
}
